'use client'
import React, { createRef, useCallback } from 'react'
import 'react-phone-input-2/lib/style.css'
import useYupValidationResolver from '@/hooks/useYupValidationResolver'
import { useFieldArray, useForm } from 'react-hook-form'
import SvgSpinner from '@/components/icons/spinner'
import { useSelector } from 'react-redux'
import { tutorSelectorById } from '@/redux/selectors/tutor'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { roleSelector, filledAnswerSelector } from '@/redux/selectors/answer'
import ErrorAlert from '@/components/ui/alerts/errorAlert'
import { useRouter } from 'next/navigation'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import {
  register,
  registerWithGoogle,
  setAuthenticated,
  setUserPending,
} from '@/redux/slices/auth'
import { toast } from 'react-hot-toast'
import { validationSchemaForSingUp as validationSchemaForSingUp } from '@/components/ui/common-form-signup/validationScheme'
import RegisterWithEmailPass, {
  Roles,
} from '@/components/ui/common-form-signup/components/registerWithEmailPass'
import { GoogleIcon } from '@/components/icons'
import { notify } from '@/utils/index'
import SvgSpinnerBlue from '@/components/icons/spinner-blue'
import RegisterWithRole from '@/components/ui/common-form-signup/components/RegisterWithRole'
import { handleUserName } from '@/firebase/tutor'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  referMail,
  sendEmailToSupport,
  sendGeneralEmail,
  sentEmailBookedSlotStudent,
  sentEmailBookedSlotTutor,
} from '@/services/novu-notifications'
import { firebaseGetUserInfoFromDb } from '@/firebase/utils'
import { validateCaptcha } from '@/utils/helpers'
import jobselector from '@/redux/selectors/jobs'
import { firebaseBookLessonByStudent } from '@/firebase/students'
import { updateJobDetail } from '@/redux/slices/jobs'
import { StudentBookedTutorToSupport } from '@/utils/email-templates'
import { UserRole } from '@/src/types'
import { getSubscriptionPlanWithPricesByPlanId } from '@/firebase/plans'
import { TrackGoogleAnalyticsEvent } from '@/utils/google-analytics'
import { SubscriptionInitiated } from '@/utils/google-analytics/events/subscription'
import { createCheckoutSession, goToBillingPortal } from '@/services/stripe'
import { getStudentSubscription } from '@/firebase/customers'
import { SUBSCRIPTIONS_PATH } from '@/utils/mixpanel/events/paywallSource'

const embeddedSubscriptionPageFinalDestinationUrl =
  process.env.NEXT_PUBLIC_EMBEDDED_PAGE_FINAL_DESTINATION_URL || ''
const embeddedSubscriptionPageGoBackUrl =
  process.env.NEXT_PUBLIC_EMBEDDED_PAGE_GO_BACK_URL || ''

const embeddedPageSubscriptionMismatchErrorText =
  'The selected subscription plan is only available for purchase by students.'

const onError = (error) => {
  console.error('sign up on error', error)
}

function CommonSignUpForm({
  onClose,
  isStudent,
  alreadySignUp = false,
  requestCallback = false,
  bookedTutor = false,
  tutorId = '',
  messageTutor = '',
  setSignIn = () => {},
  setIsMessageTutor = () => {},
  setIsBookedTutor = () => {},
}) {
  const recaptchaRef = createRef()
  const [refEmail, setRefEmail] = useState('')
  const [refName, setRefName] = useState('')

  const resolver = useYupValidationResolver(validationSchemaForSingUp)
  const [errorMessage, setErrorMessage] = useState('')
  const [refStudentId, setRefStudentId] = useState('')
  const { control, handleSubmit, formState, setValue, getValues } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver,
    values: {
      role: Roles[0],
    },
    defaultValues: {
      role: Roles[0],
    },
  })
  const { fields, append, remove } = useFieldArray({
    name: 'connects',
    control,
  })
  const { errors } = formState
  const params = useSearchParams()
  const dispatch = useDispatch()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const refId = params.get('ref')
  const courseId = params.get('courseId')
  const topicId = params.get('topicId')
  const lessonId = params.get('lessonId')
  const paperId = params.get('paperId')
  const subscriptionPlanId = params.get('subscriptionPlanId')
  const [form, setForm] = useState(null)
  const [googleAuthenticationChallenged, setGoogleAuthenticationChallenged] =
    useState(false)
  const [refUserId, setRefUserId] = useState('')

  // selector
  const selectedTutor = useSelector((state) =>
    tutorSelectorById(state, tutorId),
  )
  const { jobDetail } = useSelector(jobselector)
  const selectedRole = useSelector(roleSelector)
  const filledAnswers = useSelector(filledAnswerSelector)

  const handleEmbeddedPageRedirectToSubscription = useCallback(
    async (subscriptionPlanId: string, userId: string, userRole: UserRole) => {
      // return to origin source and show error reason
      if (userRole !== UserRole.Student) {
        notify('error', embeddedPageSubscriptionMismatchErrorText)
        router.replace(`/`)

        return
      }

      try {
        setLoading(true)

        const subscriptionPlan = await getSubscriptionPlanWithPricesByPlanId(
          subscriptionPlanId,
        )

        const userSubscription = await getStudentSubscription(userId)

        if (userSubscription) {
          TrackGoogleAnalyticsEvent(
            SubscriptionInitiated,
            SubscriptionInitiated,
            {
              userId,
            },
          )

          await goToBillingPortal({
            returnUrl: embeddedSubscriptionPageFinalDestinationUrl,
            subscriptionId: userSubscription.subscriptionId,
            priceId: process.env.NEXT_PUBLIC_EXAM_PACK_MONTHLY_PRICE_ID,
          })
        } else {
          await createCheckoutSession({
            uid: userId,
            priceId: subscriptionPlan.prices[0]?.id,
            interval: subscriptionPlan?.prices[0]?.interval,
            plan: subscriptionPlan?.role,
            url: SUBSCRIPTIONS_PATH,
            paywallTriggerSource: 'signup_form',
            additionalUrls: {
              success_url: embeddedSubscriptionPageFinalDestinationUrl,
              cancel_url: embeddedSubscriptionPageGoBackUrl,
            },
          })

          TrackGoogleAnalyticsEvent(
            SubscriptionInitiated,
            SubscriptionInitiated,
            {
              userId,
            },
          )
        }
      } catch (error) {
        throw error
      } finally {
        setLoading(false)
      }
    },
    [router],
  )

  const signUpWithGoogle = async () => {
    try {
      setLoadingGoogle(true)
      setGoogleAuthenticationChallenged(true)
      const role = getValues('role')?.value
      const registrationParams = role ? { role } : undefined

      const user = await dispatch(
        registerWithGoogle(registrationParams),
      ).unwrap()

      if (user && !user.error) {
        localStorage.setItem('user', JSON.stringify(user))
        dispatch(setAuthenticated(true))
        dispatch(setUserPending(false))

        let path = '/'

        // notify user about subscription and role mismatch
        if (
          !!subscriptionPlanId &&
          user.role.toLowerCase() !== UserRole.Student
        ) {
          notify('error', embeddedPageSubscriptionMismatchErrorText)
        }

        switch (user.role?.toLowerCase()) {
          case UserRole.Student:
            setSignIn(false)

            // handle redirect from embedded exam-pack-subscription page
            if (!!subscriptionPlanId) {
              await handleEmbeddedPageRedirectToSubscription(
                subscriptionPlanId,
                user?.uid,
                user?.role,
              )

              break
            }

            if (bookedTutor && tutorId) {
              setIsBookedTutor(true)
              // path = `student/book-tutor/tutoring-type?id=${tutorId}`
            } else if (messageTutor && !bookedTutor) {
              setIsMessageTutor(true)
            } else {
              path = '/student'
              const params = new URLSearchParams()

              if (courseId) params.append('courseId', courseId)
              if (topicId) params.append('topicId', topicId)
              if (lessonId) params.append('lessonId', lessonId)
              if (paperId) params.append('paperId', paperId)

              if ([...params].length) path += `?${params}`
            }

            break
          case UserRole.Parent:
            path = 'parent/dashboard'
            break
          case UserRole.School:
            path = 'parent/dashboard'
            break
          case UserRole.Tutor:
            path = 'tutors-dashboard'
            break
        }

        const isFromSub = localStorage.getItem('isFromSubscribe')
        if (isFromSub) {
          router.replace('/subscriptions')
        } else {
          router.replace(path)
        }
      }
      setLoadingGoogle(false)
    } catch (error) {
      notify('error', error)
      console.error(error, 'errorWhileLogin')
      setLoadingGoogle(false)
    }
  }

  useEffect(() => {
    if (refId) {
      let refUserId = window.atob(unescape(encodeURIComponent(refId)))
      setRefUserId(refUserId)
      const getUserDetail = async () => {
        const userDetail = await firebaseGetUserInfoFromDb(refUserId)
        setRefEmail(userDetail?.email)
        setRefStudentId(userDetail?.uid)
        setRefName(userDetail?.username)
      }
      getUserDetail()
    }
  }, [refId])

  // submit handler
  const onSubmit = async (objUserDetail, event) => {
    event.preventDefault()
    setLoading(true)
    recaptchaRef.current.execute()
    setForm(objUserDetail)
  }

  // hooks
  useEffect(() => {
    if (selectedTutor.id) {
      setValue('selectedTutor', [selectedTutor.id])
    }
    if (selectedRole) {
      setValue('role', selectedRole.name)
    }
  }, [selectedTutor, selectedRole, filledAnswers, setValue])

  const onReCAPTCHAChange = async (captchaCode) => {
    if (!captchaCode) return toast.error('There was an error. please try again')

    // Validating recaptcha code
    const response = await validateCaptcha(captchaCode)

    if (!response.success) {
      setLoading(false)

      return toast.error('There was an error. please try again')
    }

    try {
      let userDetail = { ...form }
      if (refUserId) {
        userDetail = { ...userDetail, refId: refUserId }
      }

      // If the registration mode is student and selected role is student
      if (isStudent && userDetail.role?.value === UserRole.Student) {
        const studentData = userDetail
        const username = await handleUserName(studentData?.email.split('@')[0])
        const response = await dispatch(
          register({
            ...studentData,
            username,
            role: UserRole.Student,
          }),
        ).unwrap()

        if (response.status === 'success') {
          toast.success('You are successfully registered')
          localStorage.setItem('user', JSON.stringify(studentData))

          if (refUserId) {
            const dataObj = {
              name: studentData?.name,
              email: studentData?.email,
              refStudentId: refStudentId,
              refEmail: refEmail,
              refName: refName,
            }
            await referMail(dataObj)
          }

          if (jobDetail) {
            bookTutorByStudent(
              {
                studentsId: response?.uid,
                studentName: studentData?.name,
                ...jobDetail,
              },
              response,
            )
            setTimeout(() => {
              dispatch(updateJobDetail(null))
            }, 1000)
          }

          // handle redirect from embedded exam-pack-subscription page
          if (!!subscriptionPlanId && response.uid && response.role) {
            await handleEmbeddedPageRedirectToSubscription(
              subscriptionPlanId,
              response.uid,
              response.role,
            )

            return
          }

          onClose(false)
        } else {
          setErrorMessage(response?.error)
          setLoading(false)
        }
        return null
      }

      // Register any other role or student in non-student mode
      if (!googleAuthenticationChallenged) {
        setLoading(true)
        const { email, name, role, ...rest } = userDetail
        const username = await handleUserName(email.split('@')[0])

        const response = await dispatch(
          register({
            ...rest,
            email,
            name,
            phone: userDetail?.phone || '',
            password: userDetail.password,
            username: username,
            role: role?.value ? role?.value : UserRole.Student,
            profileImg: '',
            coverImage: '',
            avgReview: 0.0,
            status: 'approved',
            applicationStatus: 'completed',
            gender: '',
          }),
        ).unwrap()
        setLoading(false)
        onClose(false)

        if (response.status === 'success') {
          localStorage.setItem('user', JSON.stringify(userDetail))

          if (jobDetail && (!role?.value || role?.value === UserRole.Student)) {
            bookTutorByStudent(
              {
                studentsId: response?.uid,
                studentName: name,
                ...jobDetail,
              },
              response,
            )
            setTimeout(() => {
              dispatch(updateJobDetail(null))
            }, 1000)
          }

          if (refUserId) {
            const dataObj = {
              name: name,
              email: email,
              refStudentId: refStudentId,
              refEmail: refEmail,
              refName: refName,
            }
            await referMail(dataObj)
          }
          if (requestCallback && role?.value === UserRole.Student) {
            if (messageTutor && !bookedTutor) {
              setSignIn(false)
              setIsMessageTutor(true)
            } else if (bookedTutor) {
              setSignIn(false)
              setIsBookedTutor(true)
            } else {
              router.push('student/dashboard')
            }

            await sendGeneralEmail({
              inAppMsg: 'Student requested a call',
              inAppRedirectTo: `/`,
              emailSubject: 'Student requested a call',
              emailMsg: `<table align="center" width="100%" class="" data-id="react-email-section" border="0" cellPadding="0" cellSpacing="0" role="presentation">
              <tbody>
                <tr>
                  <td>
                    <p data-id="react-email-text" style="font-size:1.125rem;line-height:1.75rem;margin:16px 0;color:rgb(52,64,84);font-weight:600">${name} requested a callback.</p>
                    <p data-id="react-email-text" style="font-size:1.125rem;line-height:1.75rem;margin:16px 0;color:rgb(52,64,84);font-weight:600">Details:</p>
                    <p data-id="react-email-text" style="font-size:1.125rem;line-height:1.75rem;margin:16px 0;color:rgb(52,64,84);font-weight:400"></p>
                    <table cellSpacing="0" cellPadding="0">
                      <tr style="color:rgb(52,64,84);font-weight:400;font-size:1.125rem;line-height:1.75rem">
                        <td width="24" align="center" valign="top"><span style="font-size:1.875rem;line-height:1.5rem"> •</span></td>
                        <td align="left" valign="top">Name: ${name}</td>
                      </tr>
                      <tr style="color:rgb(52,64,84);font-weight:400;font-size:1.125rem;line-height:1.75rem">
                        <td width="24" align="center" valign="top"><span style="font-size:1.875rem;line-height:1.5rem"> •</span></td>
                        <td align="left" valign="top">Phone: ${userDetail?.phone}</td>
                      </tr>
                    </table>
                    <p></p>
                  </td>
                </tr>
              </tbody>
            </table>`,
              recipientEmail: 'info@examsolutions.net',
              userId: 'ei7Jua23aJbmnqusJnu0P6N3UKa2',
            })
          } else {
            toast.success('You are successfully registered')
          }
        } else {
          setErrorMessage(JSON.stringify(response?.error))
        }
      }
    } catch (error) {
      console.error('An error occurred:', error)
      setErrorMessage(error.message)
    } finally {
      recaptchaRef?.current?.reset()
    }
  }

  const bookTutorByStudent = async (lessonData, user) => {
    try {
      const response = await firebaseBookLessonByStudent(lessonData)
      if (response.success) {
        // Send the notification to the tutor
        await sentEmailBookedSlotTutor({
          name: lessonData?.tutorsName,
          studentName: lessonData?.studentName,
          date: moment(lessonData?.date).format('MMM Do, YYYY'),
          topic: '',
          subjects: lessonData?.subjects,
          timeSlots: [lessonData?.slot],
          recipientEmail: lessonData?.tutorsEmail,
          userId: lessonData?.tutorsId,
          link: `${window.location.origin}/my-jobs/${response.jobId}`,
        })

        // Send the notification to the student
        await sentEmailBookedSlotStudent({
          name: lessonData?.studentName,
          tutorName: lessonData?.tutorsName,
          date: moment(lessonData?.date).format('MMM Do, YYYY'),
          timeSlots: [lessonData?.slot],
          topic: '',
          subjects: lessonData?.subjects,
          recipientEmail: user?.email,
          userId: user?.uid,
        })

        // send the notification
        await sendEmailToSupport({
          emailSubject: 'Student Booked Tutor',
          emailBody: StudentBookedTutorToSupport({
            tutorName: lessonData?.tutorsName,
            studentName: lessonData?.studentName,
            timeSlots: [lessonData?.slot],
            date: moment(lessonData?.date).format('MMM Do, YYYY'),
          }),
        })

        toast.success(
          'The tutor has received your request and will get back to you shortly.',
        )
      }
    } catch (error) {
      console.error(error)
      toast.error('Something went wrong!')
    }
  }

  return (
    <div className="flex flex-col justify-center flex-1 mt-5 sm:mt-0 item-center">
      <div className="w-full mt-2 sm:mx-auto md:w-[500px]">
        <div className="py-2 bg-white sm:rounded-lg">
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <div className="">
              {!isStudent ? (
                <RegisterWithRole
                  control={control}
                  fields={fields}
                  errors={errors}
                  requestCallback={requestCallback}
                />
              ) : (
                <div>
                  <RegisterWithEmailPass
                    control={control}
                    fields={fields}
                    errors={errors}
                  />
                </div>
              )}
            </div>

            {/* <div className="mb-4"> */}
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              onChange={onReCAPTCHAChange}
            />
            {/* </div> */}

            {!isStudent ? (
              <button
                disabled={loading}
                type="submit"
                className="w-full py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm disabled:bg-v8-mut bg-v8-blue focus:outline-none focus:ring-offset-0 focus:ring-0 hover:bg-v8-blue transition duration-300"
              >
                {loading && <SvgSpinner />}
                Sign up
              </button>
            ) : (
              <>
                <button
                  disabled={loading}
                  type="submit"
                  className="w-full py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm disabled:bg-v8-mut bg-v8-blue focus:outline-none focus:ring-offset-0 focus:ring-0 hover:bg-v8-blue transition duration-300"
                >
                  {loading && <SvgSpinner />}
                  Sign up
                </button>
                <button
                  disabled={loading}
                  onClick={() => signUpWithGoogle()}
                  className="w-full mt-2 py-2 text-sm font-medium text-v8-ink border border-solid border-[#D5D9EB] rounded-md shadow-sm  focus:outline-none focus:ring-offset-0 focus:ring-0 flex justify-center gap-3 hover:bg-v8-blue/5 transition duration-300"
                >
                  <span>{loadingGoogle && <SvgSpinnerBlue />}</span>
                  <span>
                    {' '}
                    <GoogleIcon />{' '}
                  </span>{' '}
                  Sign up with Google
                </button>
              </>
            )}
          </form>
        </div>
        {alreadySignUp && (
          <div className="ml-2 text-sm font-medium text-v8-ink flex justify-center pt-4">
            Already have an account?
            <button
              onClick={() => {
                let path = '/login'

                // handle case when opened as exam-pack embedded page
                if (!!subscriptionPlanId) {
                  path += `?subscriptionPlanId=${subscriptionPlanId}`

                  router.push(path)
                  return
                }

                if (courseId) {
                  path += `?courseId=${courseId}`
                  if (topicId) {
                    path += `&topicId=${topicId}`
                    if (lessonId) {
                      path += `&lessonId=${lessonId}`
                    }
                  }
                }
                if (paperId) {
                  path += `?paperId=${paperId}`
                }
                router.push(path)
              }}
              className="text-v8-blue outline-none pl-3"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
      <div className={'w-full sm:mx-auto md:w-[550px]'}>
        {errorMessage && (
          <ErrorAlert
            title={errorMessage}
            onClose={() => setErrorMessage('')}
          />
        )}
      </div>
    </div>
  )
}

export default CommonSignUpForm
